import axios, { AxiosResponse } from 'axios';
import { apiClient } from '../endpoints';
import { ToolType } from './toolsTypes';
import { DailyTool, Site } from '../planning';
import { User } from '../users';

export interface Tool {
    crudStatus: number;
    _id: string;
    codeProC: string;
    designation: string;
    toolTypeId: ToolType;
    price?: number;
    priceType?: number;
    usageCount?: {
        total: number;
        dailyTransfersPlanned: number;
        dailyTransfersRides: number;
        dailyTools: number;
    };
    model?: string;
    startDate?: string;
    endDate?: string;
    updatedAt?: string;
    createdAt?: string;
    baseName: string;
}

interface ToolsResponse {
    message: string;
    data: Tool[];
}

export interface GetToolsQueries {
    active?: boolean;
    withUsage?: boolean;
}

export const getAllTools = async (queries?: GetToolsQueries): Promise<ToolsResponse> => {
    try {
        const response: AxiosResponse<ToolsResponse> = await apiClient.get('/tools', { params: queries });
        return response.data;
    } catch (error) {
        if (axios.isAxiosError(error) && error.response) {
            throw new Error(error.response.data.message);
        } else {
            throw new Error('An unexpected error occurred');
        }
    }
};

export const getActiveTools = async (): Promise<ToolsResponse> => {
    try {
        const response: AxiosResponse<ToolsResponse> = await apiClient.get('/tools/active');
        return response.data;
    } catch (error) {
        if (axios.isAxiosError(error) && error.response) {
            throw new Error(error.response.data.message);
        } else {
            throw new Error('An unexpected error occurred');
        }
    }
};

export interface UpdateToolBody {
    designation?: string;
    codeProC?: string;
    toolTypeId?: string;
    userId?: string;
    crudStatus?: number;
    model?: string;
    startDate?: string | null;
    endDate?: string | null;
    baseName?: string;
}

export interface ToolResponse {
    message: string;
    data: Tool;
}

export const updateTool = async (toolId: string, data: UpdateToolBody): Promise<ToolResponse> => {
    try {
        const response: AxiosResponse<ToolResponse> = await apiClient.patch('/tools/' + toolId, data);
        return response.data;
    } catch (error) {
        if (axios.isAxiosError(error) && error.response) {
            throw new Error(error.response.data.message);
        } else {
            throw new Error('An unexpected error occurred');
        }
    }
};

export interface CreateToolBody {
    designation: string;
    codeProC: string;
    toolTypeId: string;
    crudStatus?: number;
    model?: string;
    startDate?: string | null;
    endDate?: string | null;
    baseName: string;
}

export const createTool = async (data: CreateToolBody): Promise<ToolResponse> => {
    try {
        const response: AxiosResponse<ToolResponse> = await apiClient.post('/tools', data);
        return response.data;
    } catch (error) {
        if (axios.isAxiosError(error) && error.response) {
            throw new Error(error.response.data.message);
        } else {
            throw new Error('An unexpected error occurred');
        }
    }
};

export interface ToolWithLastLocation {
    _id: string;
    latestState: number;
    latestDate: string;
    designation: string;
    type: string;
    conducFirstName: string;
    conducLastName: string;
    conducColor: string;
    conducUsername: string;
    codeProC: string;
    siteName: string;
    siteId: string;
}

export interface ToolsWithLastLocationsResponse {
    message: string;
    data: ToolWithLastLocation[];
}

export const getToolWithLastLocation = async (date: Date): Promise<ToolsWithLastLocationsResponse> => {
    try {
        const response: AxiosResponse<ToolsWithLastLocationsResponse> = await apiClient.get('/daily-tools/locations/date/' + date.toISOString());
        return response.data;
    } catch (error) {
        if (axios.isAxiosError(error) && error.response) {
            throw new Error(error.response.data.message);
        } else {
            throw new Error('An unexpected error occurred');
        }
    }
};

export interface MonthlyTool extends Tool {
    dailyToolsByDate: {
        date: string;
        daily: { dailyTool: DailyTool; user: User; site: Site }[];
    }[];
}

export interface GetMonthlyToolParams {
    date: string;
}

export interface MonthlyToolsResponse {
    data: MonthlyTool[];
    message: string;
}

export const getMonthlyTools = async (params: GetMonthlyToolParams): Promise<MonthlyToolsResponse> => {
    try {
        const response: AxiosResponse<MonthlyToolsResponse> = await apiClient.get('/tools/monthly/' + params.date);
        return response.data;
    } catch (error) {
        if (axios.isAxiosError(error) && error.response) {
            throw new Error(error.response.data.message);
        } else {
            throw new Error('An unexpected error occurred');
        }
    }
};

